import CustomAmountInput from './CustomAmountInput';
import serialize from '../utils/form/serialize';
import ResponseHandlerRetriever from './hostedPayment/responseHandlers/ResponseHandlerRetriever';
import disableForm from '../utils/form/disableForm';
import Service from './OnePageGivingService';
import Messaging from './OnePageGivingMessaging';
import FormFieldsComponent from './components/FormFieldsComponent';
import OnePageGivingComponent from './components/OnePageGivingComponent';
import dom from '../utils/dom';
import Fields from './OnePageGivingFormFields';
import OnePageGivingButtons from './OnePageGivingButtons';

/** @typedef {import("../tnew/currency/CurrencyFormatter").default} CurrencyFormatter */
/** @typedef {import("./SuggestedAmountButton").default} SuggestedAmountButton */
/** @typedef {import("../tnew/lib/FieldValidationProvider").default} FieldValidationProvider */
/** @typedef {import('./createRecaptchaWrapper').RecaptchaWrapper} RecaptchaWrapper */

const FieldValidationProvider = window.tnew.lib.FieldValidationProvider;


class OnePageGivingForm {
  /**
   * @param {HTMLFormElement} $form
   * @param {CurrencyFormatter} currencyFormatter
   * @param {RecaptchaWrapper} recaptcha
   */
  constructor($form, currencyFormatter, recaptcha) {
    this.$el = $form;
    this.currencyFormatter = currencyFormatter;
    this.recaptcha = recaptcha;

    this.$totalPending = dom.findFrom(this.$el, '.tn-opg-form__total-amount--pending');
    this.$totalValid = dom.findFrom(this.$el, '.tn-opg-form__total-amount--valid');

    /** @type {FieldValidationProvider} */
    const validationProvider = FieldValidationProvider.createStandardProvider(this.$el);

    this.responseHandlerRetriever = new ResponseHandlerRetriever(validationProvider);

    this.customAmountInput = new CustomAmountInput(this.$el, validationProvider, this.onCustomInputUpdate);

    const buttons = new OnePageGivingButtons(this.$el, this.onButtonClick);

    this.suggestedAmountButtons = buttons.suggestedAmountButtons;
    this.currentButton = buttons.currentButton;

    this.$el.addEventListener('submit', this.onSubmit);
  }


  /**
   * @param {SuggestedAmountButton} btn
   */
  onButtonClick = (btn) => {
    const { currentButton } = this;

    if (btn === currentButton) {
      return;
    }

    Messaging.clearAmountEntryErrors();

    if (btn.isOtherAmountButton) {
      this.customAmountInput.expand();
    } else {
      this.customAmountInput.collapse();
    }

    this.currentButton = btn;

    if (btn.isOtherAmountButton) {
      this.updateTotalForCustomInput();
    } else {
      this.setAmount(btn.value);
      this.displayTotal();
    }
  }

  setAmount(value) {
    this.amount = value;

    this.updateTotalDisplay();
    this.displayTotal();
  }

  updateTotalDisplay() {
    const { amount } = this;
    const formatted = this.currencyFormatter.format(amount);

    this.$totalValid.textContent = formatted;
  }

  displayPending() {
    dom.toggle(this.$totalPending, true);
    dom.toggle(this.$totalValid, false);
  }

  displayTotal() {
    dom.toggle(this.$totalPending, false);
    dom.toggle(this.$totalValid, true);
  }

  onCustomInputUpdate = () => {
    if (this.currentButton && !this.currentButton.isOtherAmountButton) { // TODO track mode not buttons
      return;
    }

    this.updateTotalForCustomInput();
  }

  updateTotalForCustomInput() {
    const { isAmountValid, value } = this.customAmountInput;

    if (isAmountValid) {
      this.setAmount(value);

      this.displayTotal();
    } else {
      this.displayPending();
    }
  }

  getAmountData() {
    const {
      suggestedAmountButtons,
      customAmountInput,
      currentButton,
    } = this;

    const customAmountResult = {
      amount: customAmountInput.rawValue,
      isAmountCustom: true,
    };

    if (!suggestedAmountButtons.length) {
      return customAmountResult;
    }

    if (!currentButton) {
      return { amount: 0, isAmountCustom: false };
    }

    if (currentButton && currentButton.isOtherAmountButton) {
      return customAmountResult;
    }

    return {
      amount: currentButton.value,
      isAmountCustom: false,
    };
  }

  /**
   * @param {HTMLFormElement} $form
   */
  getData() {
    const formData = serialize(this.$el);
    const { amount, isAmountCustom } = this.getAmountData();
    const recaptchaResponse = this.recaptcha.getResponse();

    return (Object.assign({}, formData, {
      [Fields.IsAmountCustom]: isAmountCustom,
      [Fields.Amount]: amount,
      [Fields.RecaptchaResponse]: recaptchaResponse,
    }));
  }

  testShowInvalidAmountErrorMessage() {
    const { amount, isAmountCustom } = this.getAmountData();

    if (isAmountCustom) {
      return false;
    }

    return parseFloat(amount) === 0;
  }

  onSubmit = async (event) => {
    event.preventDefault();
    Messaging.clearErrors();

    const showInvalidAmount = this.testShowInvalidAmountErrorMessage();

    const formData = this.getData();
    const { reenableForm } = disableForm(this.$el);

    /** @type {import("./types").SubmitResponse} */
    let response;
    /** @type {import("./types").ResponseHandlerObject} */
    let responseHandler;

    try {
      response = await Service.submit(formData);
      responseHandler = this.responseHandlerRetriever.get(response);
    } catch (e) {
      Messaging.addPageError(OnePageGivingComponent.errorMessage);

      this.recaptcha.reset();

      reenableForm();

      return;
    }

    await responseHandler.handle(response, this.recaptcha);

    if (showInvalidAmount) {
      Messaging.addAmountEntryError(FormFieldsComponent.requiredFieldValidationMessage);
    }

    reenableForm();

    FieldValidationProvider.focusFirstError();
  }
}

export default OnePageGivingForm;




// WEBPACK FOOTER //
// ./src/onePageGiving/OnePageGivingForm.js